import { AddIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Flex,
	Text,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { usePatient, usePatientCommands } from "../hooks/patients.hooks";
import PageTitle from "../components/PageTitle";
import CakeIcon from "../components/icons/CakeIcon";
import { format } from "date-fns";
import DataTable, {
	type Cell,
	SearchBar,
} from "../components/table/ServerSideTable";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { Command } from "../types/command.types";
import OrderStatusBadge from "../components/OrderStatusBadge";
import CommandTypeBadge from "../components/CommandTypeBadge";
import InlineUserAvatar from "../components/InlineUserAvatar";
import PrimaryButton from "../components/PrimaryButton";
import { useNewOrderStore } from "../stores/order.store";
import { useCenter } from "../hooks/centers.hooks";
import { useAdminSettings } from "../hooks/admin_settings.hooks";
import { useSimpleToast } from "../hooks/toast.hooks";

const CenterText = ({ id }: { id: string | number | undefined }) => {
	const { data: center } = useCenter(id);
	if (center === null || center === undefined) {
		return <>(Lié a aucun centre)</>;
	}
	return <>{center.name}</>;
};

const PatientDetailPage = () => {
	const { id } = useParams();
	const { data: patient } = usePatient(id);
	const ActionsComponent = () => {
		const { reset, setPatient } = useNewOrderStore();
		const navigate = useNavigate();
		const { data: adminSettings } = useAdminSettings();
		const toast = useSimpleToast();

		return (
			<Flex>
				<PrimaryButton
					onClick={() => {
						if (adminSettings?.disableCommandCreation) {
							toast.error(
								"Commande",
								"La création de commandes est désactivée.",
							);
							return;
						}
						if (patient) {
							// 0. reset
							reset();
							// 1. set patient
							setPatient(patient);
							// 2. navigate to new order
							navigate("/orders/new");
						}
					}}
				>
					<AddIcon mr={4} />
					<Text>Créer une commande</Text>
				</PrimaryButton>
			</Flex>
		);
	};
	const columns = useMemo<ColumnDef<Command>[]>(() => {
		return [
			{
				accessorFn: (row) => row.exposedId,
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<OrderStatusBadge halted={row.haltedAt != null} status={row.status} />
				),
				id: "statut",
				header: "Statut",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <CommandTypeBadge type={row.type} />,
				id: "type",
				header: "Type",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Text>
						{row.draft?.date ? format(row.draft.date, "dd/MM/yyyy") : ""}
					</Text>
				),
				id: "shippingDate",
				header: "Livraison",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},

			{
				accessorFn: (row) => <InlineUserAvatar id={row.creatorId} />,
				id: "creator",
				header: "Créé par",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		];
	}, []);

	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/patients"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Patients
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						{id}
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<PageTitle>
				{patient?.firstName} {patient?.lastName}
			</PageTitle>
			<Flex direction="row" justifyContent="start" alignItems="center" gap={2}>
				<CakeIcon color="#475569" />
				<Text color="#475569">
					{patient?.birthDate ? format(patient?.birthDate, "dd/MM/yyyy") : ""}
				</Text>
				<Text color="#475569">
					<CenterText id={patient?.centerId}></CenterText>
				</Text>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={usePatientCommands(id)}
				w={["300px", "240px", "200px", "200px", "auto"]}
				flexGrow={[0, 0, 0, 0, 1]}
				defaultSortBy={[{ id: "id", desc: true }]}
				cellLink={(cell: Cell) => {
					return `/orders/${cell.row.id}`;
				}}
				toolbar={
					<Flex my="1rem" justifyContent="space-between">
						<SearchBar />
						<ActionsComponent />
					</Flex>
				}
			/>
		</>
	);
};

export default PatientDetailPage;
